/**
 * @fileoverview Componente responsável por oferecer uma variação estilizada de select.
 */

import React from "react";
import { FormControl, InputLabel, Select, makeStyles } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiInputLabel-outlined": {
      color: theme.palette.type === "dark" ? "#94a3b8" : "#64748b",
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color: theme.palette.type === "dark" ? "#a5b4fc" : "#3D5CFD",
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      color: theme.palette.type === 'dark' ? '#f8fafc' : '#0f172a',
      '& fieldset': {
        borderColor: 'transparent',
      },
      '& .MuiSelect-select': {
        color: theme.palette.type === 'dark' ? '#f8fafc' : '#0f172a',
      },
      '& .MuiSelect-icon': {
        color: theme.palette.type === 'dark' ? '#94a3b8' : '#64748b',
      },
      '&:hover': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(61, 92, 253, 0.04)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.25)' : '1px solid rgba(61, 92, 253, 0.15)',
      },
      '&.Mui-focused': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 1)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.4)' : '1px solid rgba(61, 92, 253, 0.3)',
        boxShadow: '0 0 0 4px rgba(91, 124, 255, 0.15), 0 8px 24px rgba(91, 124, 255, 0.2)',
      },
    },
  },
  menuPaper: {
    marginTop: theme.spacing(1),
    borderRadius: 14,
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, rgba(18, 18, 19, 0.98) 0%, rgba(24, 24, 27, 0.98) 100%)"
      : "#ffffff",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(91, 124, 255, 0.18)"
      : "1px solid rgba(15, 23, 42, 0.08)",
    boxShadow: theme.palette.type === "dark"
      ? "0 18px 40px rgba(0, 0, 0, 0.4)"
      : "0 18px 40px rgba(15, 23, 42, 0.12)",
  },
  menuList: {
    padding: theme.spacing(0.75),
    "& .MuiMenuItem-root": {
      borderRadius: 10,
      color: theme.palette.type === "dark" ? "#e5e7eb" : "#0f172a",
      fontSize: "0.9rem",
      minHeight: 38,
    },
    "& .MuiMenuItem-root:hover": {
      background: theme.palette.type === "dark"
        ? "rgba(91, 124, 255, 0.16)"
        : "rgba(61, 92, 253, 0.08)",
    },
    "& .MuiMenuItem-root.Mui-selected": {
      background: theme.palette.type === "dark"
        ? "rgba(91, 124, 255, 0.22)"
        : "rgba(61, 92, 253, 0.12)",
    },
    "& .MuiMenuItem-root.Mui-selected:hover": {
      background: theme.palette.type === "dark"
        ? "rgba(91, 124, 255, 0.28)"
        : "rgba(61, 92, 253, 0.16)",
    },
  },
}));

const ModernSelect = ({ label, children, className, formControlProps, MenuProps, displayEmpty, ...props }) => {
  const classes = useStyles();
  const mergedMenuProps = {
    ...MenuProps,
    getContentAnchorEl: MenuProps?.getContentAnchorEl ?? null,
    anchorOrigin: MenuProps?.anchorOrigin ?? { vertical: "bottom", horizontal: "left" },
    transformOrigin: MenuProps?.transformOrigin ?? { vertical: "top", horizontal: "left" },
    PaperProps: {
      ...(MenuProps?.PaperProps || {}),
      className: [classes.menuPaper, MenuProps?.PaperProps?.className].filter(Boolean).join(" "),
    },
    MenuListProps: {
      ...(MenuProps?.MenuListProps || {}),
      className: [classes.menuList, MenuProps?.MenuListProps?.className].filter(Boolean).join(" "),
    },
  };

  return (
    <FormControl variant="outlined" className={`${classes.root} ${className || ''}`} {...formControlProps}>
      {label && <InputLabel shrink={displayEmpty ? true : undefined}>{label}</InputLabel>}
      <Select label={label} displayEmpty={displayEmpty} MenuProps={mergedMenuProps} {...props}>
        {children}
      </Select>
    </FormControl>
  );
};

export default ModernSelect;
